<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { watch, ref, computed, inject, onBeforeUnmount } from "vue"
import { useRoute } from "vue-router"
import { useI18n } from "vue-i18n"
import { storeToRefs } from "pinia"
import { useEventBus } from "@vueuse/core"
import moment from "moment"
import * as _ from "lodash-es"
import type { Services } from "@services"
import type { Indicator } from "@types"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useGraphStore } from "@modules/data/stores/graph.store"
import { useFetchWrapper } from "@core/composables/useFetchWrapper"
import type {
    ChauffageGraphsResponse,
    ChauffagePeriodeData,
} from "@modules/energy-manager/services/chauffage.service"
import { GRAPH_REGLAGE_CHAUFFERIE } from "@modules/data/stores/graph.store"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Réglage chaufferie",
            one_week: "1 semaine",
            one_month: "1 mois",
            six_month: "6 mois",
            one_year: "1 an",
            period_label: "Période",
            retry: "Réessayer",
            error_load: "Impossible de charger les graphiques.",
            no_sensor:
                "Aucun capteur de température. Ajoutez un capteur pour afficher les graphiques.",
            add_sensor: "Ajouter un capteur",
            reduit_nuit: "Réduit de nuit",
            balancing: "Équilibrage",
            curve_heat: "Courbe de chauffe",
            kpi_balancing_label: "Écart entre étages",
            kpi_balancing_subtitle: "Écart étage le plus chaud − le plus froid",
            kpi_balancing_tooltip:
                "Différence entre la température moyenne la plus élevée et la plus basse par étage, sur la période. Un écart faible indique un meilleur équilibrage entre les niveaux du bâtiment.",
            kpi_curve_heat_label: "Lien intérieur / extérieur",
            kpi_curve_heat_subtitle:
                "Pente sur les jours de chauffe (T_ext < 18 °C)",
            kpi_curve_heat_tooltip:
                "Pente de la droite qui relie, jour par jour, la température intérieure moyenne à la température extérieure moyenne, en ne gardant que les jours où la moyenne extérieure est inférieure à 18 °C. Elle indique comment la température intérieure évolue avec le froid extérieur sur la période analysée.",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const services = inject<Services>("services")!
const route = useRoute()
const buildingStore = useBuildingStore()
const graphStore = useGraphStore()
const { building, isFetchingBuilding } = storeToRefs(buildingStore)
const bus = useEventBus<string>("news")

const period = ref<[Date, Date]>([
    moment().subtract(6, "months").toDate(),
    moment().toDate(),
])

const chauffageResponse = ref<ChauffageGraphsResponse | null>(null)

/** Modal d’ajout de capteur */
const sourceAppendModalVisible = ref(false)

const dateFrom = computed(() => moment(period.value[0]).format("YYYY-MM-DD"))
const dateTo = computed(() => moment(period.value[1]).format("YYYY-MM-DD"))

const {
    fetch: fetchChauffage,
    isFetching: isFetchingChauffage,
    hasError: hasErrorChauffage,
    error: errorChauffage,
    cancel: cancelChauffageFetch,
} = useFetchWrapper(async (_kwargs?: unknown, controller?: AbortController) => {
    const id = building.value?._id
    if (!id) return
    chauffageResponse.value = null
    const response = await services.$getChauffageGraphs(
        id,
        dateFrom.value,
        dateTo.value,
        controller
    )
    chauffageResponse.value = response
})

/** Charge le bâtiment, la liste des graphiques (config) puis les données chauffage. */
const { fetch: fetchInitialData, isFetching: isFetchingInitialData } =
    useFetchWrapper(async () => {
        if (!route.params._id_building) return
        if (!graphStore.isGraphListLoaded) {
            await graphStore.fetchGraphList()
        }
        if (building.value?._id && !isNeedSensor.value) await fetchChauffage()
    })

const isFetching = computed(
    () => isFetchingBuilding.value || isFetchingChauffage.value
)

const graphTitles: Record<string, string> = {
    reglage_chaufferie_temperature_reduit_nuit: "Réduit de nuit",
    reglage_chaufferie_balancing: "Équilibrage du bâtiment",
    reglage_chaufferie_profil_daily: "Profil quotidien et réduit de nuit",
    reglage_chaufferie_profil_weekly_hour: "Profil hebdomadaire par heure",
    reglage_chaufferie_profil_weekly_day: "Profil hebdomadaire par jour",
    reglage_chaufferie_curve_heat: "Courbe de chauffe du bâtiment",
}

const orderedChauffageGraphs = computed(() => {
    const graphs = chauffageResponse.value?.graphs ?? []
    return [...graphs].sort(
        (a, b) =>
            GRAPH_REGLAGE_CHAUFFERIE.indexOf(
                a.id as (typeof GRAPH_REGLAGE_CHAUFFERIE)[number]
            ) -
            GRAPH_REGLAGE_CHAUFFERIE.indexOf(
                b.id as (typeof GRAPH_REGLAGE_CHAUFFERIE)[number]
            )
    )
})

/**
 * Variables (types de canaux) issues des sources du bâtiment.
 * Permet de savoir si la page réglage chaufferie peut afficher les graphiques.
 */
const getBuildingVariablesFromSources = computed(() => {
    const b = building.value
    if (!b?.sources?.length) return []
    const types = new Set<string>()
    for (const s of b.sources) {
        const channels = (s as { channels?: { type: string }[] }).channels ?? []
        for (const c of channels) {
            if (c.type && c.type !== "None") types.add(c.type)
        }
    }
    return [...types]
})

/**
 * True si le bâtiment a besoin d’un capteur (température extérieure ou autres variables chauffage)
 * pour afficher les graphiques. Quand true, on n’appelle pas l’API graphiques et on affiche le message « Ajouter un capteur ».
 */
const isNeedSensor = computed(() => {
    const v = getBuildingVariablesFromSources.value
    if (v.length === 0) return true
    const hasTempExterieur = v.includes("temperature_air_exterieur")
    const hasTempInterieur = v.includes("temperature_air_interieur")
    const hasEnergieGaz = v.includes("energie_gaz_chauffage")
    const hasTempEauDepart = v.includes("temperature_eau_departchauffage")
    if (!hasTempExterieur) return true
    if (hasTempInterieur || hasEnergieGaz || hasTempEauDepart) return false
    return true
})

/** Config des graphiques réglage chaufferie (depuis fetchGraphList). */
const getChauffageGraphConfig = computed(() =>
    graphStore.getGraphConfigByCategory("GRAPH_REGLAGE_CHAUFFERIE")
)

/** Graphiques profil : pas d’indicateurs KPI sur la page (données DAPI sans clés periode dédiées). */
const GRAPHS_WITHOUT_KPI = new Set<string>([
    "reglage_chaufferie_profil_daily",
    "reglage_chaufferie_profil_weekly_hour",
    "reglage_chaufferie_profil_weekly_day",
])

/**
 * Formate une valeur numérique periode pour affichage indicateur.
 * @param v - Nombre ou undefined
 * @param decimals - Décimales (défaut 2)
 */
function formatPeriodeNumber(
    v: number | undefined,
    decimals: number = 2
): string {
    if (v === undefined || v === null || Number.isNaN(Number(v))) {
        return "-"
    }
    return Number(v).toFixed(decimals)
}

/**
 * Construit l’indicateur équilibrage (écart °C entre étages) depuis periode.data.
 */
function getBalancingIndicatorFromPeriode(
    data: ChauffagePeriodeData | undefined,
    graph: { id: string; datasets?: unknown[] }
): Indicator {
    const kpi = data?.balancing?.kpi
    const firstDs = graph.datasets?.[0] as
        | { icon?: string; icon_color?: string }
        | undefined
    return {
        graph_id: graph.id,
        type: "balancing.kpi",
        name: t("kpi_balancing_label"),
        subtitle: t("kpi_balancing_subtitle"),
        tooltipHint: t("kpi_balancing_tooltip"),
        value: formatPeriodeNumber(kpi),
        unit: "°C",
        icon: firstDs?.icon ?? "",
        icon_color: firstDs?.icon_color ?? "",
    }
}

/**
 * Construit l’indicateur courbe de chauffe (pente dT_int/dT_ext) depuis periode.data.
 */
function getCurveHeatIndicatorFromPeriode(
    data: ChauffagePeriodeData | undefined,
    graph: { id: string; datasets?: unknown[] }
): Indicator {
    const kpi = data?.curve_heat?.kpi
    const firstDs = graph.datasets?.[0] as
        | { icon?: string; icon_color?: string }
        | undefined
    return {
        graph_id: graph.id,
        type: "curve_heat.kpi",
        name: t("kpi_curve_heat_label"),
        subtitle: t("kpi_curve_heat_subtitle"),
        tooltipHint: t("kpi_curve_heat_tooltip"),
        value: formatPeriodeNumber(kpi, 3),
        unit: "",
        icon: firstDs?.icon ?? "",
        icon_color: firstDs?.icon_color ?? "",
    }
}

/**
 * Indicateurs KPI dérivés de la config graphique et des données periode.
 * Même structure que useKPI pour affichage via DataIndicatorList.
 * Pour équilibrage et courbe de chauffe, les chemins Mongo (ex. *.mean) sont ignorés
 * au profit de balancing.kpi et curve_heat.kpi renvoyés par DAPI.
 * Les graphiques profil quotidien / hebdo n’affichent aucun indicateur.
 */
const kpiIndicators = computed<{ name: string; indicator: Indicator[] }[]>(
    () => {
        const config = getChauffageGraphConfig.value
        const data: ChauffagePeriodeData | undefined =
            chauffageResponse.value?.periode?.data
        const displayedGraphIds = new Set(
            orderedChauffageGraphs.value.map((g) => g.id)
        )
        if (!config || Object.keys(config).length === 0) {
            return []
        }
        return Object.values(config)
            .filter((card) =>
                card.graph_list?.some((g) => displayedGraphIds.has(g.id))
            )
            .map((card) => {
                const indicatorList: Indicator[] = []
                for (const graph of card.graph_list ?? []) {
                    if (!displayedGraphIds.has(graph.id)) continue

                    if (GRAPHS_WITHOUT_KPI.has(graph.id)) {
                        continue
                    }

                    if (graph.id === "reglage_chaufferie_balancing") {
                        indicatorList.push(
                            getBalancingIndicatorFromPeriode(data, graph)
                        )
                        continue
                    }
                    if (graph.id === "reglage_chaufferie_curve_heat") {
                        indicatorList.push(
                            getCurveHeatIndicatorFromPeriode(data, graph)
                        )
                        continue
                    }

                    for (const dataset of graph.datasets ?? []) {
                        if (dataset.type === "annotation") continue
                        const d = dataset as {
                            type: string
                            name: string
                            unit: string
                            icon?: string
                            icon_color?: string
                        }
                        const raw = data ? _.get(data, d.type) : undefined
                        const value =
                            raw !== undefined &&
                            raw !== null &&
                            !Number.isNaN(Number(raw))
                                ? Number(raw).toFixed(2)
                                : "-"
                        indicatorList.push({
                            graph_id: graph.id,
                            type: d.type,
                            name: d.name,
                            value: String(value),
                            unit: d.unit ?? "",
                            icon: d.icon ?? "",
                            icon_color: d.icon_color ?? "",
                        })
                    }
                }
                return { name: card.name, indicator: indicatorList }
            })
            .filter((group) => group.indicator.length > 0)
    }
)

/**
 * Cartes à afficher : en chargement (skeleton via PlotlyChartCard) ou avec les
 * graphiques chargés.
 */
const chartCards = computed(() => {
    if (isFetchingChauffage.value && !chauffageResponse.value) {
        return GRAPH_REGLAGE_CHAUFFERIE.map((id) => ({
            id,
            title: graphTitles[id] ?? id,
            graph: null as unknown,
            loading: true,
        }))
    }
    const graphs = orderedChauffageGraphs.value
    return graphs.map((g) => ({
        id: g.id,
        title: graphTitles[g.id] ?? g.id,
        graph: typeof g.data === "string" ? JSON.parse(g.data) : g.data,
        loading: false,
    }))
})

/**
 * Scroll vers la carte du graphique correspondant au KPI cliqué.
 * @param payload - type et graph_id de l'indicateur
 */
function onClickKPI(payload: { type: string; graph_id: string }) {
    bus.emit("graph:scroll", payload)
    const el = document.getElementById(`graph-${payload.graph_id}`)
    el?.scrollIntoView({ behavior: "smooth", block: "start" })
}

function setPeriodPreset(preset: "1w" | "1m" | "6m" | "1y") {
    const end = moment()
    let start = moment()
    switch (preset) {
        case "1w":
            start = moment().subtract(7, "days")
            break
        case "1m":
            start = moment().subtract(1, "month")
            break
        case "6m":
            start = moment().subtract(6, "months")
            break
        case "1y":
            start = moment().subtract(1, "year")
            break
    }
    period.value = [start.toDate(), end.toDate()]
}

function onRetry() {
    fetchChauffage()
}

function onAddSensor() {
    sourceAppendModalVisible.value = true
}

/**
 * Fin du parcours capteur : même rechargement que succès.
 */
async function onSourceAppendDone(): Promise<void> {
    await fetchInitialData()
}

watch(
    () => route.params?._id_building,
    (newId) => {
        if (!newId) return
        fetchInitialData()
    },
    { immediate: true }
)

/** Recharge les graphiques chauffage quand la période change (building déjà chargé par le watch route). */
watch(
    () => period.value,
    () => {
        if (building.value?._id && !isNeedSensor.value) {
            fetchChauffage()
        }
    },
    { deep: true }
)

/** Annule et vide les données quand on quitte le bâtiment (nav ou autre). */
watch(
    () => building.value?._id,
    (id) => {
        if (!id) {
            chauffageResponse.value = null
            cancelChauffageFetch()
        }
    }
)

onBeforeUnmount(() => {
    graphStore.cancelPendingGraphRequests()
})
</script>

<template>
    <div class="page page-reglage-chaufferie">
        <HeaderPageBuilding :title="t('title')" />

        <!-- ================================ -->
        <!-- ÉTAT DE CHARGEMENT -->
        <!-- ================================ -->
        <template v-if="isFetchingInitialData">
            <BaseSkeleton width="100%" height="3rem" />
            <BaseSkeleton width="100%" height="40rem" />
            <BaseSkeleton width="100%" height="40rem" />
        </template>

        <BaseMessage
            v-if="buildingStore.hasErrorBuilding"
            icon="pi pi-exclamation-triangle"
            severity="error"
            class="p-4"
        >
            {{ buildingStore.errorBuilding?.message }}
        </BaseMessage>

        <template v-else>
            <!-- Bâtiment sans capteur : message uniquement, pas de chargement des graphiques -->
            <BaseMessage
                v-if="isNeedSensor"
                icon="pi pi-info-circle"
                severity="warn"
                :pt="{
                    text: 'flex flex-col gap-2',
                }"
            >
                <p class="text-base font-medium">{{ t("no_sensor") }}</p>
                <BaseButton
                    :label="t('add_sensor')"
                    severity="warn"
                    @click="onAddSensor"
                    class="w-fit"
                    size="small"
                />
            </BaseMessage>

            <!-- Bâtiment avec capteur : filtre période, KPI et graphiques -->
            <template v-else>
                <!-- Filtre période -->
                <div class="flex flex-wrap items-end gap-4 mb-6">
                    <BaseFloatLabel variant="on" class="min-w-[16rem]">
                        <BaseDatePicker
                            id="reglage-chaufferie-period"
                            v-model="period"
                            show-icon
                            selection-mode="range"
                            date-format="dd/mm/yy"
                            :manual-input="false"
                            :max-date="moment().toDate()"
                            hide-on-range-selection
                            :placeholder="t('period_label')"
                        />
                        <label for="reglage-chaufferie-period">{{
                            t("period_label")
                        }}</label>
                    </BaseFloatLabel>
                    <div class="flex gap-2">
                        <BaseButton
                            outlined
                            severity="secondary"
                            :label="t('one_week')"
                            @click="setPeriodPreset('1w')"
                        />
                        <BaseButton
                            outlined
                            severity="secondary"
                            :label="t('one_month')"
                            @click="setPeriodPreset('1m')"
                        />
                        <BaseButton
                            outlined
                            severity="secondary"
                            :label="t('six_month')"
                            @click="setPeriodPreset('6m')"
                        />
                        <BaseButton
                            outlined
                            severity="secondary"
                            :label="t('one_year')"
                            @click="setPeriodPreset('1y')"
                        />
                    </div>
                </div>

                <!-- Erreur chargement -->
                <BaseMessage
                    v-if="hasErrorChauffage"
                    severity="error"
                    icon="pi pi-times-circle"
                    class="mb-6"
                >
                    {{ errorChauffage?.message ?? t("error_load") }}
                    <BaseButton
                        class="mt-2"
                        size="small"
                        :label="t('retry')"
                        @click="onRetry"
                    />
                </BaseMessage>

                <!-- Pas de capteur (aucune donnée) -->
                <BaseMessage
                    v-else-if="
                        !isFetching &&
                        chauffageResponse?.graphs?.length === 0 &&
                        !hasErrorChauffage
                    "
                    icon="pi pi-info-circle"
                    severity="warn"
                    class="mb-6"
                >
                    <p>{{ t("no_sensor") }}</p>
                    <BaseButton
                        class="mt-2"
                        size="small"
                        severity="warn"
                        :label="t('add_sensor')"
                        @click="onAddSensor"
                    />
                </BaseMessage>
                <!-- Indicateurs KPI (config depuis fetchGraphList, affichage comme PageBuildingData) -->
                <DataIndicatorList
                    v-if="kpiIndicators.length > 0"
                    :indicators="kpiIndicators"
                    :loading="isFetchingChauffage && !chauffageResponse"
                    :error="hasErrorChauffage"
                    class="mb-6"
                    @click:kpi="onClickKPI"
                />

                <!-- Cartes de graphiques (skeleton ou données via PlotlyChartCard) -->
                <template v-if="chartCards.length">
                    <PlotlyChartCard
                        v-for="card in chartCards"
                        :key="card.id"
                        :card-id="`graph-${card.id}`"
                        class="mb-6"
                        :title="card.title"
                        :graph="card.graph"
                        :loading="card.loading"
                        content-min-height="20rem"
                    />
                </template>
            </template>
        </template>

        <SourceAppendDialog
            v-model:visible="sourceAppendModalVisible"
            from-state="source"
            :allowed-source-types="['source']"
            :building-id="(route.params._id_building as string) ?? undefined"
            :address="building?.address ?? null"
            @done="onSourceAppendDone"
        />
    </div>
</template>
